import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type MeResponse } from './api';
import { useNavigate } from './router';

type AuthContextValue = {
  me: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setMe(await api<MeResponse>('/auth/me'));
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api('/auth/logout', { method: 'POST' });
    // Explicit sign-out is a hard browser-session boundary. Never preserve the
    // previous staff/platform route as a return target for the next identity.
    sessionStorage.removeItem('nuramend-platform-preview-org');
    sessionStorage.removeItem('nuramend-auth-context');
    sessionStorage.setItem('nuramend-explicit-signout', '1');
    const platformSession = me?.user.platformRole !== 'NONE';
    setMe(null);
    setLoading(false);
    navigate(platformSession ? '/admin?signedOut=1' : '/login?signedOut=1', { replace: true });
  };

  useEffect(() => { void refresh(); }, []);
  const value = useMemo(() => ({ me, loading, refresh, logout }), [me, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
